import { Field } from 'formik';
import PropTypes from 'prop-types';
import React, { lazy, useEffect, useState } from 'react';
import { FormikDatePicker } from 'globalcomponents/UI/Form/Base';
import { useTranslation } from 'react-i18next';
import underscore from 'underscore';
import Policies from './Policies';
import { COOKIE_AUTH_DETAILS, ROLES } from 'globalconfig/constants/keys';
import { UI_DATE_LOWER_FORMAT } from 'globalconfig/constants/static';
import { getCookie } from 'globalservices/cookie';
import {
  getDateLocale,
  getFilterLocaleValues,
  hasEnabledFeature,
  hasRestrictedRole,
  isdvirOnly,
  isdvirOnlyUser,
  phoneNumberValidation,
} from 'globalutils/fmUtils';
import { hasAnyValueMatch } from 'globalutils/underscoreUtil';
import { checkServiceProvider } from './serviceProviderConfig';

const layout = window?.$environment?.CURRENT_LAYOUT;
const Input = lazy(() => import(`globalcomponents/UI/Input/${layout}`));
const Label = lazy(() => import(`globalcomponents/UI/FormLabel/${layout}`));
const Button = lazy(() => import(`globalcomponents/UI/Button/${layout}`));
const CheckBox = lazy(() => import(`globalcomponents/UI/Toggle/${layout}`));
const Message = lazy(() => import(`globalcomponents/UI/Message/${layout}`));
const Select = lazy(() => import(`globalcomponents/UI/Select/${layout}`));
const Department = lazy(() => import(`globalmodules/Department`));
const UploadModule = lazy(() => import('./UploadModule'));
const MemoizedUploadModule = React.memo(UploadModule);
const SubHeader = lazy(() => import(`globalcomponents/SubHeader/${layout}`));
const FormStyle = require(`globalcomponents/UI/Form/${layout}/serverStyle`);

const RenderComponent = ({ values, handleEdit, errors, isNotValid, validateForm, customProps, submitAction, cancelAction }) => {
  const token = getCookie(COOKIE_AUTH_DETAILS);
  const { i18n } = useTranslation();
  const {
    type,
    allCycleRules,
    allCycleExceptions,
    allVehicleTypes,
    terminals,
    notificationList,
    departments,
    subDepartments,
    disabledCriteria,
    regions,
    Styled,
    insuracePortalUserRole,
    onFleetChange,
    fleetVehicleList,
    user,
  } = customProps;
  const SERVICE_PROVIDER = window?.$environment?.SERVICE_PROVIDER;
  const isDVIRFlagEnabled = isdvirOnly();
  const isDVIRUserFlagEnabled = isdvirOnlyUser();
  const isDisabled = checkServiceProvider('render_isDisabled', SERVICE_PROVIDER, false);
  const RTFMuser = checkServiceProvider('RTFMuser', SERVICE_PROVIDER, false);
  const preventEditingFmToDriver = SERVICE_PROVIDER === 'spireon' && type === 'edit';
  const disableRoleSwitchOnEdit = SERVICE_PROVIDER === 'spireon' && type === 'edit';
  const vehicleNames = values?.idsPubVehiclesOfMechanic ? values.idsPubVehiclesOfMechanic.map((o) => o.label) : [];

  useEffect(() => {
    if (values.regionId) handleEdit(values.regionId, { field: 'regionId' });
    else if (values.regionId === null) handleEdit('', { field: 'regionId' });

    if (values.terminalId) handleEdit(values.terminalId, { field: 'terminalId' });
    else if (values.terminalId === null) handleEdit('', { field: 'terminalId' });

    if (values.departmentId) handleEdit(values.departmentId, { field: 'departmentId' });
    else if (values.departmentId === null) handleEdit('', { field: 'departmentId' });

    if (values.subDepartmentId) handleEdit(values.subDepartmentId, { field: 'subDepartmentId' });
    else if (values.subDepartmentId === null) handleEdit('', { field: 'subDepartmentId' });
  }, [values.regionId, values.terminalId, values.departmentId, values.subDepartmentId]);

  useEffect(() => {
    if (!values.fleetManagerRole) {
      let btn = document.getElementById('checkbox_Fleet Manager (Default)');
      btn && btn.parentNode.classList.remove('checked');
      btn && btn.parentNode.classList.add('unchecked');
      handleEdit(false, {
        field: 'fleetManagerRoleDefault',
        type: 'checkbox',
      });
    } else if (values.fleetManagerRole && !values.fleetManagerRoleDefault) {
      let btn = document.getElementById('checkbox_Fleet Manager (Default)');
      btn && btn.parentNode.classList.remove('checked');
      btn && btn.parentNode.classList.add('unchecked');
    }
  }, [values.fleetManagerRole]);

  const [uploadList, setUploadList] = useState({ currentDocs: [] });
  const [isDocumentAvailable, setIsDocumentAvailable] = useState(true);

  const changeRole = (currentRole, callback, event) => {
    switch (currentRole.field) {
      case 'driverRole':
        callback(false, { field: 'fleetManagerRole' });
        callback(false, { field: 'adminRole' });
        callback(false, { field: 'fleetManagerRoleDefault' });
        if (!event) callback(false, { field: 'exemptFromEld' });
        callback(event, { field: 'driverRole' });
        break;
      case 'adminRole':
        callback(false, { field: 'driverRole' });
        callback(event, currentRole);
        if (values.mechanicRole)
          setUploadList({
            ...uploadList,
            currentDocs: [...uploadList.commonDocs.slice(0, 1), ...uploadList.mechanicDocs, ...uploadList.commonDocs.slice(1)],
          });
        else
          setUploadList({
            ...uploadList,
            currentDocs: [...uploadList.commonDocs],
          });
        break;
      case 'fleetManagerRole':
        callback(false, { field: 'driverRole' });
        callback(event, currentRole);
        if (!event) {
          callback(false, { field: 'fleetManagerRoleDefault' });
        }

        if (event) callback(event, { field: 'fleetManagerRole' });
        else {
          callback(event, { field: 'fleetManagerRole' });
          callback(event, { field: 'fleetManagerRoleDefault' });
        }

        if (values.mechanicRole)
          setUploadList({
            ...uploadList,
            currentDocs: [...uploadList.commonDocs.slice(0, 1), ...uploadList.mechanicDocs, ...uploadList.commonDocs.slice(1)],
          });
        else setUploadList({ ...uploadList, currentDocs: uploadList.commonDocs });
        break;
      case 'fleetManagerRoleDefault':
        callback(false, { field: 'driverRole' });
        callback(event, currentRole);
        setUploadList({ ...uploadList, currentDocs: uploadList.commonDocs });
        break;
      case 'mechanicRole':
        callback(event, currentRole);
        break;
      case 'serviceManagerRole':
        callback(event, currentRole);
        break;
      case 'underWriterRole':
        callback(event, currentRole);
        break;
      case 'lossControlRole':
        callback(event, currentRole);
        break;
      case 'claimAdjuster':
        callback(event, currentRole);
        break;
      default:
        break;
    }
  };

  const showError = (field, validationErrors, isNotValid, apiError) => {
    if (validationErrors[field] && !isNotValid) {
      return <Message type="error" message={errors[field]} />;
    }
    if (apiError?.[field]) {
      return <Message type="error" message={apiError[field]} />;
    }
  };

  const checkCanadaCompliance = () => {
    if (type == 'edit' && values.initialUserDriver && hasEnabledFeature('canadaComplianceEnabled')) return true;
    else return false;
  };

  const checkIsDefaultFM = () => {
    return !hasRestrictedRole(['OWNER']) && values.fleetManagerRoleDefault;
  };

  return (
    <>
      <Styled.Wrapper className="vehicleWrap">
        <Styled.LeftWrapper>
          <div className={customProps?.newUI || window.$environment.WHITE_THEME ? 'section-wrapper' : ''}>
            <SubHeader
              className="subheaderSection spireonDriver notspireonNew userHeading"
              text={i18n.t('common.userInformation')}
              variant="h2"
            />
            <div className="formWrap notspireonNew">
              <FormStyle.default.FieldsWrapper width="100%">
                <Label variant="body1">{i18n.t('common.firstName')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="firstName"
                    handleOnChange={(event) => {
                      handleEdit(event, { field: 'firstName' });
                    }}
                    as={Input}
                    placeholderText={i18n.t('common.firstName')}
                    value={values['firstName']}
                    type="text"
                    disabled={
                      customProps.viewOnly ||
                      isDisabled ||
                      checkIsDefaultFM() ||
                      (RTFMuser && values.driverRole) ||
                      false
                    }
                  />
                  {showError('firstName', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>
              <FormStyle.default.FieldsWrapper width="100%">
                <Label variant="body1">{i18n.t('common.lastName')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="lastName"
                    handleOnChange={(event) => {
                      handleEdit(event, { field: 'lastName' });
                    }}
                    as={Input}
                    placeholderText={i18n.t('common.lastName')}
                    value={values['lastName']}
                    type="text"
                    disabled={
                      customProps.viewOnly || isDisabled || checkIsDefaultFM() || (RTFMuser && values.driverRole) || false
                    }
                  />
                  {showError('lastName', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>
              <FormStyle.default.FieldsWrapper width="100%">
                <Label variant="body1">{i18n.t('common.cellPhone')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="cellPhone"
                    handleOnChange={(event) => {
                      handleEdit(event, {
                        field: 'phone',
                      });
                    }}
                    as={Input}
                    placeholderText={i18n.t('common.cellPhone')}
                    value={values['phone']}
                    updateValue={phoneNumberValidation}
                    type="text"
                    disabled={customProps.viewOnly || isDisabled || checkIsDefaultFM() || false}
                  />
                  {showError('phone', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>
              <FormStyle.default.FieldsWrapper width="100%">
                <Label variant="body1">{i18n.t('common.email')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="email"
                    handleOnChange={(event) => {
                      handleEdit(event, { field: 'email' });
                    }}
                    as={Input}
                    placeholderText={i18n.t('common.email')}
                    value={values['email']}
                    type="text"
                    disabled={customProps.viewOnly || isDisabled || checkIsDefaultFM() || false}
                  />
                  {showError('email', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>

              {checkServiceProvider('render_hireDate', SERVICE_PROVIDER, false) && (
                <FormStyle.default.FieldsWrapper width="100%">
                  <Label variant="body1">{i18n.t('common.hireDate')}</Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="hireDate"
                      onChange={(event) => {
                        handleEdit(event, { field: 'hireDate', type: 'date' });
                      }}
                      locale={getDateLocale()}
                      as={FormikDatePicker}
                      placeholderText={UI_DATE_LOWER_FORMAT}
                      value={values['hireDate']}
                      type="date"
                      showPopperArrow={false}
                      showTimeSelect={false}
                      showDisabledMonthNavigation
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      isClearable={true}
                      disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                    />
                    {showError(
                      'hireDate',
                      errors,

                      isNotValid,
                      customProps.hasApiStatus?.errorMessage
                    )}
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>
              )}
              {values.driverRole ? (
                <>
                  <FormStyle.default.FieldsWrapper width="100%" className="licenceNumber">
                    <Label variant="body1">{i18n.t('common.licenceNumber')}</Label>
                    <FormStyle.default.TextWrapper className="userForm">
                      <Field
                        name="licenceNumber"
                        handleOnChange={(event) => {
                          handleEdit(event, { field: 'licenseNumber' });
                        }}
                        as={Input}
                        placeholderText={i18n.t('common.licenceNumber')}
                        value={values['licenseNumber']}
                        type="text"
                        disabled={
                          customProps.viewOnly ||
                          isDisabled ||
                          checkIsDefaultFM() ||
                          (RTFMuser && values.driverRole) ||
                          false
                        }
                        updateValue={(value) => value.replace(/[^a-zA-Z0-9]/g, '')}
                      />
                      {showError(
                        'licenseNumber',
                        errors,

                        isNotValid,
                        customProps.hasApiStatus?.errorMessage
                      )}
                    </FormStyle.default.TextWrapper>
                  </FormStyle.default.FieldsWrapper>
                  <FormStyle.default.FieldsWrapper width="100%" className="stateCode">
                    <Label variant="body1">{i18n.t('common.state')}</Label>
                    <FormStyle.default.TextWrapper className="userForm">
                      <Field
                        name="licenseState"
                        onChange={(event) => {
                          handleEdit(event, { field: 'licenseState' });
                        }}
                        as={Select}
                        placeholder={i18n.t('common.pleaseSelect')}
                        value={values['licenseState']}
                        suggestions={customProps.states}
                        disabled={
                          customProps.viewOnly ||
                          isDisabled ||
                          checkIsDefaultFM() ||
                          (RTFMuser && values.driverRole) ||
                          false
                        }
                      />
                      {showError(
                        'licenseState',
                        errors,

                        isNotValid,
                        customProps.hasApiStatus?.errorMessage
                      )}
                    </FormStyle.default.TextWrapper>
                  </FormStyle.default.FieldsWrapper>
                </>
              ) : null}
              {customProps.type === 'edit' && (
                <FormStyle.default.FieldsWrapper width="100%">
                  <Label variant="body1">{i18n.t('common.otherContact')}</Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="otherContact"
                      handleOnChange={(event) => {
                        handleEdit(event, { field: 'otherContact' });
                      }}
                      as={Input}
                      placeholderText={i18n.t('common.otherContact')}
                      value={values['otherContact']}
                      type="text"
                      disabled={customProps.viewOnly || isDisabled || checkIsDefaultFM() || false}
                    />
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>
              )}
              <FormStyle.default.FieldsWrapper width="100%" className="roleSection userRoleSection">
                <Label variant="body1">{i18n.t('common.role')}</Label>
                <div className="roles">
                  <FormStyle.default.CheckBoxWrapper direction="row" className="roleWrapper">
                    {hasRestrictedRole(['OWNER']) && !hasRestrictedRole([ROLES.SERVICE_MANAGER]) && !insuracePortalUserRole && (
                      <CheckBox
                        type="NormalGrey"
                        checked={values.fleetManagerRoleDefault && values.fleetManagerRole}
                        disabled={
                          disableRoleSwitchOnEdit ||
                          values.driverRole ||
                          !values.fleetManagerRole ||
                          isDisabled ||
                          checkCanadaCompliance()
                        }
                        label={i18n.t('common.fleetManagerDefault')}
                        onClick={(event) => {
                          if (disableRoleSwitchOnEdit) return;
                          changeRole(
                            {
                              field: 'fleetManagerRoleDefault',
                              type: 'checkbox',
                            },
                            handleEdit,
                            event
                          );
                        }}
                      />
                    )}

                    {!hasRestrictedRole([ROLES.SERVICE_MANAGER]) && !insuracePortalUserRole && (
                      <CheckBox
                        type="NormalGrey"
                        checked={values.fleetManagerRole}
                        disabled={
                          disableRoleSwitchOnEdit ||
                          checkIsDefaultFM() ||
                          (hasRestrictedRole(['OWNER', 'FLMANAGER']) ? values.driverRole : true) ||
                          isDisabled ||
                          checkCanadaCompliance()
                        }
                        label={i18n.t('common.fleetManager')}
                        onClick={(event) => {
                          if (disableRoleSwitchOnEdit) return;
                          changeRole({ field: 'fleetManagerRole', type: 'checkbox' }, handleEdit, event);
                          // setNewValues('fleetManagerRole', event)
                        }}
                      />
                    )}

                    {!hasRestrictedRole([ROLES.SERVICE_MANAGER]) &&
                      !checkServiceProvider('render_drivers', SERVICE_PROVIDER, false) && (
                        <CheckBox
                          type="NormalGrey"
                          checked={values.driverRole}
                          disabled={
                            disableRoleSwitchOnEdit ||
                            checkIsDefaultFM() ||
                            values.adminRole ||
                            values.fleetManagerRole ||
                            values.fleetManagerRoleDefault ||
                            preventEditingFmToDriver ||
                            isDisabled ||
                            values.claimAdjusterRole ||
                            values.underWriterRole ||
                            values.lossControlRole ||
                            checkCanadaCompliance()
                          }
                          label={i18n.t('users.drivers')}
                          onClick={(event) => {
                            if (preventEditingFmToDriver) return;
                            if (disableRoleSwitchOnEdit) return;
                            changeRole({ field: 'driverRole', type: 'checkbox' }, handleEdit, event);
                          }}
                        />
                      )}
                    {(isDVIRFlagEnabled && hasRestrictedRole([ROLES.SERVICE_MANAGER])) ||
                    checkServiceProvider('render_mechanic', SERVICE_PROVIDER, false) ||
                    layout !== 'Clutch' ||
                    RTFMuser ? (
                      <CheckBox
                        type="NormalGrey"
                        checked={values.mechanicRole}
                        label={i18n.t('common.mechanic')}
                        disabled={disableRoleSwitchOnEdit || checkCanadaCompliance() || checkIsDefaultFM()}
                        onClick={(event) => {
                          if (disableRoleSwitchOnEdit) return;
                          changeRole({ field: 'mechanicRole', type: 'checkbox' }, handleEdit, event);
                        }}
                        className="CheckboxNeeded"
                      />
                    ) : (
                      ''
                    )}
                    {!hasRestrictedRole([ROLES.SERVICE_MANAGER]) && !insuracePortalUserRole && (
                      <CheckBox
                        type="NormalGrey"
                        checked={values.adminRole}
                        disabled={
                          disableRoleSwitchOnEdit || checkIsDefaultFM() || values.driverRole || isDisabled || checkCanadaCompliance()
                        }
                        label={i18n.t('common.admin')}
                        onClick={(event) => {
                          if (disableRoleSwitchOnEdit) return;
                          changeRole({ field: 'adminRole', type: 'checkbox' }, handleEdit, event);
                        }}
                      />
                    )}
                    {insuracePortalUserRole && (
                      <CheckBox
                        type="NormalGrey"
                        checked={values.underWriterRole}
                        label={i18n.t('common.underWriter')}
                        disabled={values.driverRole}
                        onClick={(event) => {
                          changeRole({ field: 'underWriterRole', type: 'checkbox' }, handleEdit, event);
                        }}
                      />
                    )}
                    {insuracePortalUserRole && (
                      <CheckBox
                        type="NormalGrey"
                        checked={values.lossControlRole}
                        disabled={values.driverRole}
                        label={i18n.t('common.lossControl')}
                        onClick={(event) => {
                          changeRole({ field: 'lossControlRole', type: 'checkbox' }, handleEdit, event);
                        }}
                      />
                    )}
                    {insuracePortalUserRole && (
                      <CheckBox
                        type="NormalGrey"
                        disabled={values.driverRole}
                        checked={values.claimAdjusterRole}
                        label={i18n.t('common.claimAdjuster')}
                        onClick={(event) => {
                          changeRole({ field: 'claimAdjusterRole', type: 'checkbox' }, handleEdit, event);
                        }}
                      />
                    )}
                    {hasRestrictedRole([ROLES.SERVICE_MANAGER]) && (
                      <CheckBox
                        type="NormalGrey"
                        checked={values.serviceManagerRole}
                        label={i18n.t('common.serviceManager')}
                        disabled={disableRoleSwitchOnEdit || checkIsDefaultFM()}
                        onClick={(event) => {
                          if (disableRoleSwitchOnEdit) return;
                          changeRole({ field: 'serviceManagerRole', type: 'checkbox' }, handleEdit, event);
                        }}
                      />
                    )}
                  </FormStyle.default.CheckBoxWrapper>
                  {showError('roles', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </div>
              </FormStyle.default.FieldsWrapper>
              {insuracePortalUserRole && values.driverRole ? (
                <FormStyle.default.FieldsWrapper width="100%">
                  <Label variant="body1">{i18n.t('policyHolder')}</Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="company"
                      onChange={(event) => {
                        handleEdit(event, { field: 'company' });
                        onFleetChange(event.value);
                      }}
                      as={Select}
                      disabled={type === 'edit'}
                      placeholder={i18n.t('common.pleaseSelect')}
                      value={values['company']}
                      suggestions={customProps.companies}
                    />
                    {showError(
                      'company',
                      errors,

                      isNotValid,
                      customProps.hasApiStatus?.errorMessage
                    )}
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>
              ) : null}

              {customProps.type === 'edit' && (
                <FormStyle.default.FieldsWrapper width="100%">
                  <Label variant="body1">{i18n.t('common.timzone')}</Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="timzoneForDailylog"
                      handleOnChange={(event) => {
                        handleEdit(event, { field: 'timzoneForDailylog' });
                      }}
                      as={Input}
                      placeholderText={i18n.t('common.timezone')}
                      value={values['timzoneForDailylog']}
                      type="text"
                      disabled={customProps.viewOnly || isDisabled || checkIsDefaultFM() || customProps.type == 'edit' || false}
                    />
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>
              )}
              {customProps.type == 'edit' && values.fleetManagerRole && hasRestrictedRole(['OWNER']) ? (
                <div>
                  <FormStyle.default.FieldsWrapper width="100%" className="roleSection">
                    <Label variant="body1">{i18n.t('common.hideNotification')}</Label>
                    <div className="roles">
                      <FormStyle.default.CheckBoxWrapper direction="row" className="roleWrapper">
                        <CheckBox
                          type="NormalGrey"
                          checked={values.hideNotification}
                          disabled={customProps.viewOnly || false}
                          label={''}
                          onClick={(event) => {
                            handleEdit(event, {
                              field: 'hideNotification',
                            });
                          }}
                        />
                      </FormStyle.default.CheckBoxWrapper>
                      {showError(
                        'hideNotification',
                        errors,

                        isNotValid,
                        customProps.hasApiStatus?.errorMessage
                      )}
                    </div>
                  </FormStyle.default.FieldsWrapper>
                  <FormStyle.default.FieldsWrapper width="100%" className="filterFields">
                    <Label variant="body1" className="filterLabel">
                      {i18n.t('common.notificationList')}
                    </Label>
                    <FormStyle.default.TextWrapper className="filterField">
                      <Field
                        name="notification"
                        onChange={(event) => {
                          handleEdit(event || [], {
                            field: 'notificationId',
                          });
                        }}
                        as={Select}
                        isDisabled={false}
                        isMulti
                        placeholder={i18n.t('common.pleaseSelect')}
                        value={values.notificationId}
                        suggestions={notificationList}
                        isClearable={true}
                        disabled={customProps.viewOnly || !values.hideNotification || false}
                      />
                      {errors?.notificationId && !isNotValid && <Message type="error" message={errors.notificationId} />}
                    </FormStyle.default.TextWrapper>
                  </FormStyle.default.FieldsWrapper>
                </div>
              ) : null}
            </div>
          </div>
          {checkServiceProvider('render_userCredentials', SERVICE_PROVIDER) &&
          hasRestrictedRole(['FLMANAGER']) &&
          values.driverRole &&
          customProps.type === 'edit' ? (
            <div className={customProps?.newUI || window.$environment.WHITE_THEME ? 'section-wrapper' : ''}>
              <SubHeader text={i18n.t('common.userCredentials')} className="subheaderSection spireonDriver" variant="h2" />
              <FormStyle.default.FieldsWrapper width="100%">
                <Label variant="body1">{i18n.t('common.employeeId')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="empId"
                    handleOnChange={(event) => {
                      handleEdit(event, { field: 'empId' });
                    }}
                    as={Input}
                    disabled={isDisabled}
                    placeholder={i18n.t('common.employeeId')}
                    value={values['empId']}
                    type="text"
                  />
                  {showError('empId', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>
              <FormStyle.default.FieldsWrapper width="100%">
                <Label variant="body1">{i18n.t('common.password')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="password"
                    handleOnChange={(event) => {
                      handleEdit(event, { field: 'empPassword' });
                    }}
                    as={Input}
                    placeholderText={i18n.t('common.password')}
                    value={values['empPassword']}
                    type="text"
                  />
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>
            </div>
          ) : null}
          {(values.mechanicRole || values.driverRole || values.serviceManagerRole) &&
          !customProps?.isAddMechanic &&
          (customProps.type === 'add' ||
            (customProps.type === 'edit' && checkServiceProvider('render_employeeId', SERVICE_PROVIDER, false))) ? (
            <div className={customProps?.newUI || window.$environment.WHITE_THEME ? 'section-wrapper' : ''}>
              <SubHeader text={i18n.t('common.userCredentials')} className="subheaderSection spireonDriver" variant="h2" />
              <FormStyle.default.FieldsWrapper width="100%">
                <Label variant="body1">{i18n.t('common.employeeId')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="empId"
                    handleOnChange={(event) => {
                      handleEdit(event, { field: 'empId' });
                    }}
                    as={Input}
                    disabled={isDisabled}
                    placeholder={i18n.t('common.employeeId')}
                    value={values['empId']}
                    type="text"
                  />
                  {showError('empId', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>
              {customProps.type === 'edit' ? (
                <></>
              ) : (
                <FormStyle.default.FieldsWrapper width="100%">
                  <Label variant="body1">{i18n.t('common.password')}</Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="password"
                      handleOnChange={(event) => {
                        handleEdit(event, { field: 'empPassword' });
                      }}
                      as={Input}
                      placeholderText={i18n.t('common.password')}
                      value={values['empPassword']}
                      type="text"
                    />
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>
              )}
            </div>
          ) : null}
          {values.driverRole && (
            <div className={customProps?.newUI || window.$environment.WHITE_THEME ? 'section-wrapper' : 'notspireonNew'}>
              {values.driverRole ? (
                <>
                  <SubHeader
                    className="subheaderSection spireonDriver spireonVehicle"
                    text={i18n.t('users.vehicleForDriver')}
                    variant="h2"
                  />

                  <FormStyle.default.FieldsWrapper width="100%">
                    <Label variant="body1">{i18n.t('common.vehicleId')}</Label>
                    <FormStyle.default.TextWrapper className="userForm">
                      <Field
                        name="driverVehicleId"
                        onChange={(event) => {
                          handleEdit(event, { field: 'idPubVehicleOfDriver' });
                        }}
                        as={Select}
                        placeholder={i18n.t('common.pleaseSelect')}
                        value={values.idPubVehicleOfDriver}
                        suggestions={insuracePortalUserRole && type == 'add' ? fleetVehicleList : customProps.vehicleDriverList}
                        isClearable
                        disabled={customProps.viewOnly || isDisabled || checkIsDefaultFM() || false}
                      />
                    </FormStyle.default.TextWrapper>
                  </FormStyle.default.FieldsWrapper>
                  <FormStyle.default.FieldsWrapper width="100%">
                    <FormStyle.default.CheckBoxWrapper direction="column" className="driverCheckbox">
                      {isDVIRUserFlagEnabled && checkServiceProvider('render_isDvirOnly', SERVICE_PROVIDER, false) && (
                        <CheckBox
                          type="NormalGrey"
                          checked={values.isDvirOnly}
                          label={i18n.t('users.DVIROnly')}
                          onClick={(event) => {
                            handleEdit(event, { field: 'isDvirOnly' });
                          }}
                          disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                        />
                      )}

                      {!isDVIRFlagEnabled && (
                        <>
                          <CheckBox
                            type="NormalGrey"
                            checked={values.enableYardMoves}
                            label={i18n.t('users.enableYardMoves')}
                            onClick={(event) => {
                              handleEdit(event, { field: 'enableYardMoves' });
                            }}
                            disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                          />
                          {(hasEnabledFeature('dotTripEnabled') ||
                            values.enabledNonDotTripForCompany ||
                            user?.featureFlags?.dotTripEnabled) && (
                            <CheckBox
                              type="NormalGrey"
                              checked={values.nonDotTrip}
                              label={i18n.t('users.enableNonDOTTrip')}
                              onClick={(event) => {
                                handleEdit(event, { field: 'nonDotTrip' });
                              }}
                              disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                            />
                          )}
                          <CheckBox
                            type="NormalGrey"
                            checked={values.enablePersonalConveyance}
                            label={i18n.t('users.enablePersonalConveyance')}
                            onClick={(event) => {
                              handleEdit(event, {
                                field: 'enablePersonalConveyance',
                              });
                            }}
                            disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                          />
                          <CheckBox
                            type="NormalGrey"
                            checked={values.agExemption}
                            label={i18n.t('users.aGExemption')}
                            onClick={(event) => {
                              handleEdit(event, { field: 'agExemption' });
                            }}
                            disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                          />
                          <CheckBox
                            type="NormalGrey"
                            checked={values.bigDayShortHaul}
                            label={i18n.t('users.bigDayShortHaul')}
                            onClick={(event) => {
                              handleEdit(event, { field: 'bigDayShortHaul' });
                            }}
                            disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                          />
                          {checkServiceProvider('render_exemptFromUsingELD', SERVICE_PROVIDER, false) && (
                            <CheckBox
                              type="NormalGrey"
                              checked={values.exemptFromEld}
                              label={i18n.t('users.exemptFromUsingELD')}
                              onClick={(event) => {
                                handleEdit(event, { field: 'exemptFromEld' });
                              }}
                              disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                            />
                          )}
                        </>
                      )}
                    </FormStyle.default.CheckBoxWrapper>
                  </FormStyle.default.FieldsWrapper>
                </>
              ) : null}
              {values.exemptFromEld && (
                <FormStyle.default.FieldsWrapper width="100%">
                  <Label variant="body1">{i18n.t('common.annotation')}</Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="annotationEld"
                      handleOnChange={(event) => {
                        handleEdit(event, { field: 'annotationEld' });
                      }}
                      as={Input}
                      placeholderText={i18n.t('common.annotation')}
                      value={values['annotationEld']}
                      type="text"
                      disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                    />
                    {showError(
                      'annotationEld',
                      errors,

                      isNotValid,
                      customProps.hasApiStatus?.errorMessage
                    )}
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>
              )}
            </div>
          )}
          <div className="formWrap spireonNewonly">
            <div className="userdetailssp">
              <SubHeader className="subheaderSection spireonDriver" text={i18n.t('common.userInformation')} variant="h2" />
              <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                <Label variant="body1">{i18n.t('common.firstName')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="firstName"
                    handleOnChange={(event) => {
                      handleEdit(event, { field: 'firstName' });
                    }}
                    as={Input}
                    placeholderText={i18n.t('common.firstName')}
                    value={values['firstName']}
                    type="text"
                    disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                  />
                  {showError('firstName', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>
              <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                <Label variant="body1">{i18n.t('common.lastName')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="lastName"
                    handleOnChange={(event) => {
                      handleEdit(event, { field: 'lastName' });
                    }}
                    as={Input}
                    placeholderText={i18n.t('common.lastName')}
                    value={values['lastName']}
                    type="text"
                    disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                  />
                  {showError('lastName', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>
              <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                <Label variant="body1">{i18n.t('common.cellPhone')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="cellPhone"
                    handleOnChange={(event) => {
                      handleEdit(event, {
                        field: 'phone',
                      });
                    }}
                    as={Input}
                    placeholderText={i18n.t('common.cellPhone')}
                    value={values['phone']}
                    updateValue={phoneNumberValidation}
                    type="text"
                    disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                  />
                  {showError('phone', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>
              <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                <Label variant="body1">{i18n.t('common.email')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="email"
                    handleOnChange={(event) => {
                      handleEdit(event, { field: 'email' });
                    }}
                    as={Input}
                    placeholderText={i18n.t('common.email')}
                    value={values['email']}
                    type="text"
                    disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                  />
                  {showError('email', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>

              <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                <Label variant="body1">{i18n.t('common.hireDate')}</Label>
                <FormStyle.default.TextWrapper className="userForm">
                  <Field
                    name="hireDate"
                    onChange={(event) => {
                      handleEdit(event, { field: 'hireDate', type: 'date' });
                    }}
                    locale={getDateLocale()}
                    as={FormikDatePicker}
                    placeholderText={UI_DATE_LOWER_FORMAT}
                    value={values['hireDate']}
                    type="date"
                    showPopperArrow={false}
                    showTimeSelect={false}
                    showDisabledMonthNavigation
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    isClearable={true}
                    disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                  />
                  {showError('hireDate', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </FormStyle.default.TextWrapper>
              </FormStyle.default.FieldsWrapper>
              {values.driverRole ? (
                <>
                  <FormStyle.default.FieldsWrapper width="100%" className="licenceNumber spNew">
                    <Label variant="body1">{i18n.t('common.licenceNumber')}</Label>
                    <FormStyle.default.TextWrapper className="userForm">
                      <Field
                        name="licenceNumber"
                        handleOnChange={(event) => {
                          handleEdit(event, { field: 'licenseNumber' });
                        }}
                        as={Input}
                        placeholderText={i18n.t('common.licenceNumber')}
                        value={values['licenseNumber']}
                        type="text"
                        disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                      />
                      {showError(
                        'licenseNumber',
                        errors,

                        isNotValid,
                        customProps.hasApiStatus?.errorMessage
                      )}
                    </FormStyle.default.TextWrapper>
                  </FormStyle.default.FieldsWrapper>
                  <FormStyle.default.FieldsWrapper width="100%" className="stateCode spNew">
                    <Label variant="body1">{i18n.t('common.state')}</Label>
                    <FormStyle.default.TextWrapper className="userForm">
                      <Field
                        name="licenseState"
                        onChange={(event) => {
                          handleEdit(event, { field: 'licenseState' });
                        }}
                        as={Select}
                        placeholder={i18n.t('common.pleaseSelect')}
                        value={values['licenseState']}
                        suggestions={customProps.states}
                        disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                      />
                      {showError(
                        'licenseState',
                        errors,

                        isNotValid,
                        customProps.hasApiStatus?.errorMessage
                      )}
                    </FormStyle.default.TextWrapper>
                  </FormStyle.default.FieldsWrapper>
                </>
              ) : null}
              {customProps.type === 'edit' && (
                <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                  <Label variant="body1">{i18n.t('common.otherContact')}</Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="otherContact"
                      handleOnChange={(event) => {
                        handleEdit(event, { field: 'otherContact' });
                      }}
                      as={Input}
                      placeholderText={i18n.t('common.otherContact')}
                      value={values['otherContact']}
                      type="text"
                      disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                    />
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>
              )}
              <FormStyle.default.FieldsWrapper width="100%" className="roleSection userRoleSection">
                <Label variant="body1">{i18n.t('common.role')}</Label>
                <div className="roles">
                  <FormStyle.default.CheckBoxWrapper direction="row" className="roleWrapper">
                    {hasRestrictedRole(['OWNER']) && (
                      <CheckBox
                        type="NormalGrey"
                        checked={values.fleetManagerRoleDefault && values.fleetManagerRole}
                        disabled={disableRoleSwitchOnEdit || values.driverRole || !values.fleetManagerRole || checkIsDefaultFM()}
                        label={i18n.t('common.fleetManagerDefault')}
                        onClick={(event) => {
                          if (disableRoleSwitchOnEdit) return;
                          changeRole(
                            {
                              field: 'fleetManagerRoleDefault',
                              type: 'checkbox',
                            },
                            handleEdit,
                            event
                          );
                        }}
                      />
                    )}

                    <CheckBox
                      type="NormalGrey"
                      checked={values.fleetManagerRole}
                      disabled={
                        disableRoleSwitchOnEdit || checkIsDefaultFM() || hasRestrictedRole(['OWNER', 'FLMANAGER']) ? values.driverRole : true
                      }
                      label={i18n.t('common.fleetManager')}
                      onClick={(event) => {
                        if (disableRoleSwitchOnEdit) return;
                        changeRole({ field: 'fleetManagerRole', type: 'checkbox' }, handleEdit, event);
                      }}
                    />

                    <CheckBox
                      type="NormalGrey"
                      checked={values.driverRole}
                      disabled={
                        checkIsDefaultFM() ||
                        values.adminRole ||
                        values.fleetManagerRole ||
                        values.fleetManagerRoleDefault ||
                        preventEditingFmToDriver ||
                        disableRoleSwitchOnEdit
                      }
                      label={i18n.t('common.drivers')}
                      onClick={(event) => {
                        if (preventEditingFmToDriver) return;
                        if (disableRoleSwitchOnEdit) return;
                        changeRole({ field: 'driverRole', type: 'checkbox' }, handleEdit, event);
                      }}
                    />
                    {!isDVIRFlagEnabled || layout !== 'Clutch' || hasRestrictedRole([ROLES.SERVICE_MANAGER]) ? (
                      <CheckBox
                        type="NormalGrey"
                        checked={values.mechanicRole}
                        disabled={disableRoleSwitchOnEdit || (!hasRestrictedRole(['OWNER']) && values.fleetManagerRoleDefault)}
                        label={i18n.t('common.mechanic')}
                        onClick={(event) => {
                          if (disableRoleSwitchOnEdit) return;
                          changeRole({ field: 'mechanicRole', type: 'checkbox' }, handleEdit, event);
                        }}
                        className="CheckboxNeeded"
                      />
                    ) : (
                      ''
                    )}

                    <CheckBox
                      type="NormalGrey"
                      checked={values.adminRole}
                      disabled={disableRoleSwitchOnEdit || checkIsDefaultFM() || values.driverRole}
                      label={i18n.t('common.admin')}
                      onClick={(event) => {
                        if (disableRoleSwitchOnEdit) return;
                        changeRole({ field: 'adminRole', type: 'checkbox' }, handleEdit, event);
                      }}
                    />
                  </FormStyle.default.CheckBoxWrapper>
                  {showError('roles', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}
                </div>
              </FormStyle.default.FieldsWrapper>

              {showError('roles', errors, isNotValid, customProps.hasApiStatus?.errorMessage)}

              {customProps.type === 'edit' && (
                <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                  <Label variant="body1">{i18n.t('common.timzone')}</Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="timzoneForDailylog"
                      handleOnChange={(event) => {
                        handleEdit(event, { field: 'timzoneForDailylog' });
                      }}
                      as={Input}
                      disabled
                      placeholderText={i18n.t('common.timezone')}
                      value={values['timzoneForDailylog']}
                      type="text"
                    />
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>
              )}
              {(values.mechanicRole || values.driverRole || values.serviceManagerRole) &&
              !customProps?.isAddMechanic &&
              (customProps.type === 'add' ||
                (customProps.type === 'edit' && checkServiceProvider('render_employeeId', SERVICE_PROVIDER, false))) ? (
                <div>
                  <SubHeader text={i18n.t('common.userCredentials')} className="subheaderSection spireonDriver" variant="h2" />
                  <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                    <Label variant="body1">{i18n.t('common.employeeId')}</Label>
                    <FormStyle.default.TextWrapper className="userForm">
                      <Field
                        name="empId"
                        handleOnChange={(event) => {
                          handleEdit(event, { field: 'empId' });
                        }}
                        as={Input}
                        disabled={isDisabled}
                        placeholder={i18n.t('common.employeeId')}
                        value={values['empId']}
                        type="text"
                      />
                      {showError(
                        'empId',
                        errors,

                        isNotValid,
                        customProps.hasApiStatus?.errorMessage
                      )}
                    </FormStyle.default.TextWrapper>
                  </FormStyle.default.FieldsWrapper>
                  {customProps.type === 'edit' ? (
                    <></>
                  ) : (
                    <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                      <Label variant="body1">{i18n.t('common.password')}</Label>
                      <FormStyle.default.TextWrapper className="userForm">
                        <Field
                          name="password"
                          handleOnChange={(event) => {
                            handleEdit(event, { field: 'empPassword' });
                          }}
                          as={Input}
                          placeholderText={i18n.t('common.password')}
                          value={values['empPassword']}
                          type="password"
                        />
                      </FormStyle.default.TextWrapper>
                    </FormStyle.default.FieldsWrapper>
                  )}
                </div>
              ) : null}
              {values.mechanicRole ? (
                <div className={customProps?.newUI || window.$environment.WHITE_THEME ? 'section-wrapper' : ''}>
                  <SubHeader
                    className="subheaderSection spireonDriver notspireonNew"
                    text={i18n.t('users.vehicleForMechanic')}
                    variant="h2"
                  />
                  <FormStyle.default.FieldsWrapper width="100%" className="spNew notspireonNew">
                    <Label variant="body1">{i18n.t('common.vehicleId')}</Label>
                    <FormStyle.default.TextWrapper className="userForm">
                      <Field
                        name="vehicleId"
                        onChange={(event) => {
                          handleEdit(event, {
                            field: 'idsPubVehiclesOfMechanic',
                          });
                        }}
                        as={Select}
                        isMulti
                        disabled={checkIsDefaultFM()}
                        isClearable
                        placeholder={i18n.t('common.pleaseSelect')}
                        value={values.idsPubVehiclesOfMechanic}
                        suggestions={customProps.vehicleMechanicList}
                      />
                    </FormStyle.default.TextWrapper>
                  </FormStyle.default.FieldsWrapper>
                </div>
              ) : null}
            </div>
            <div className="vehicleForsp">
              {values.driverRole ? (
                <>
                  <SubHeader
                    className="subheaderSection spireonDriver spireonVehicle"
                    text={i18n.t('users.vehicleForDriver')}
                    variant="h2"
                  />

                  <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                    <Label variant="body1">{i18n.t('common.vehicleId')}</Label>
                    <FormStyle.default.TextWrapper className="userForm">
                      <Field
                        name="driverVehicleId"
                        onChange={(event) => {
                          handleEdit(event, { field: 'idPubVehicleOfDriver' });
                        }}
                        as={Select}
                        placeholder={i18n.t('common.pleaseSelect')}
                        value={values.idPubVehicleOfDriver}
                        suggestions={customProps.vehicleDriverList}
                        isClearable
                        disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                      />
                    </FormStyle.default.TextWrapper>
                  </FormStyle.default.FieldsWrapper>
                  {customProps.type === 'edit' && !isDVIRFlagEnabled && (
                    <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                      <Label variant="body1">{i18n.t('common.deviceType')}</Label>
                      <FormStyle.default.TextWrapper className="userForm">
                        <Field
                          name="deviceType"
                          handleOnChange={(event) => {
                            handleEdit(event, { field: 'deviceType' });
                          }}
                          as={Input}
                          disabled
                          placeholderText={i18n.t('common.deviceType')}
                          value={values['deviceType']}
                          type="text"
                        />
                      </FormStyle.default.TextWrapper>
                    </FormStyle.default.FieldsWrapper>
                  )}
                  {isDVIRUserFlagEnabled && checkServiceProvider('render_isDvirOnly', SERVICE_PROVIDER, false) && (
                    <CheckBox
                      type="NormalGrey"
                      checked={values.isDvirOnly}
                      label={i18n.t('users.DVIROnly')}
                      onClick={(event) => {
                        handleEdit(event, { field: 'isDvirOnly' });
                      }}
                      disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                    />
                  )}
                  {!isDVIRFlagEnabled && (
                    <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                      <FormStyle.default.CheckBoxWrapper direction="column" className="driverCheckbox">
                        <CheckBox
                          type="NormalGrey"
                          checked={values.enableYardMoves}
                          label={i18n.t('users.enableYardMoves')}
                          onClick={(event) => {
                            handleEdit(event, { field: 'enableYardMoves' });
                          }}
                          disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                        />
                        {(hasEnabledFeature('dotTripEnabled') || values.enabledNonDotTripForCompany) && (
                          <CheckBox
                            type="NormalGrey"
                            checked={values.nonDotTrip}
                            label={i18n.t('users.enableNonDOTTrip')}
                            onClick={(event) => {
                              handleEdit(event, { field: 'nonDotTrip' });
                            }}
                            disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                          />
                        )}
                        <CheckBox
                          type="NormalGrey"
                          checked={values.enablePersonalConveyance}
                          label={i18n.t('users.enablePersonalConveyance')}
                          onClick={(event) => {
                            // setNewValues('enablePersonalConveyance', event)
                            handleEdit(event, {
                              field: 'enablePersonalConveyance',
                            });
                          }}
                          disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                        />
                        <CheckBox
                          type="NormalGrey"
                          checked={values.agExemption}
                          label={i18n.t('users.aGExemption')}
                          onClick={(event) => {
                            handleEdit(event, { field: 'agExemption' });
                          }}
                          disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                        />
                        <CheckBox
                          type="NormalGrey"
                          checked={values.bigDayShortHaul}
                          label={i18n.t('users.bigDayShortHaul')}
                          onClick={(event) => {
                            handleEdit(event, { field: 'bigDayShortHaul' });
                          }}
                          disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                        />
                        {checkServiceProvider('render_exemptFromUsingELD', SERVICE_PROVIDER, false) && (
                          <CheckBox
                            type="NormalGrey"
                            checked={values.exemptFromEld}
                            label={i18n.t('users.exemptFromUsingELD')}
                            onClick={(event) => {
                              handleEdit(event, { field: 'exemptFromEld' });
                            }}
                            disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                          />
                        )}
                      </FormStyle.default.CheckBoxWrapper>
                    </FormStyle.default.FieldsWrapper>
                  )}
                </>
              ) : null}
              {values.exemptFromEld && (
                <FormStyle.default.FieldsWrapper width="100%" className="spNew">
                  <Label variant="body1">{i18n.t('common.annotation')}</Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="annotationEld"
                      handleOnChange={(event) => {
                        handleEdit(event, { field: 'annotationEld' });
                      }}
                      as={Input}
                      placeholderText={i18n.t('common.annotation')}
                      value={values['annotationEld']}
                      type="text"
                      disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                    />
                    {showError(
                      'annotationEld',
                      errors,

                      isNotValid,
                      customProps.hasApiStatus?.errorMessage
                    )}
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>
              )}
              {values.mechanicRole && (
                <div className={customProps?.newUI || window.$environment.WHITE_THEME ? 'section-wrapper' : ''}>
                  <SubHeader
                    className="subheaderSection spireonDriver spireonNewonly"
                    text={i18n.t('users.vehicleForMechanic')}
                    variant="h2"
                  />
                  <FormStyle.default.FieldsWrapper width="100%" className="spNew spireonNewonly">
                    <Label variant="body1">{i18n.t('common.vehicleId')}</Label>
                    {customProps?.viewOnly ? (
                      <span className="view-only-label">{vehicleNames.join(', ')}</span>
                    ) : (
                      <FormStyle.default.TextWrapper className="userForm">
                        <Field
                          name="vehicleId"
                          onChange={(event) => {
                            handleEdit(event, {
                              field: 'idsPubVehiclesOfMechanic',
                            });
                          }}
                          as={Select}
                          isMulti
                          disabled={checkIsDefaultFM()}
                          isClearable
                          placeholder={i18n.t('common.pleaseSelect')}
                          value={values.idsPubVehiclesOfMechanic}
                          suggestions={customProps.vehicleMechanicList}
                        />
                      </FormStyle.default.TextWrapper>
                    )}
                  </FormStyle.default.FieldsWrapper>
                </div>
              )}
              <>
                <SubHeader
                  className="subheaderSection spireonDriver roleSection spireonNew"
                  text={i18n.t('common.departments')}
                  variant="h2"
                />
                <div className="formWrap roleSection spireonNew">
                  <Department
                    values={values}
                    handleEdit={handleEdit}
                    i18n={i18n}
                    fetchTerminals={customProps.fetchTerminals}
                    fetchDepartments={customProps.fetchDepartments}
                    fetchSubDepartments={customProps.fetchSubDepartments}
                    regions={regions}
                    terminals={terminals}
                    departments={departments}
                    subDepartments={subDepartments}
                    disabledCriteria={disabledCriteria}
                    viewOnly={customProps?.viewOnly || checkIsDefaultFM()}
                  />
                </div>
              </>
            </div>
          </div>
          {!isDVIRFlagEnabled &&
            checkServiceProvider('render_departments', SERVICE_PROVIDER, false) &&
            !(insuracePortalUserRole && !values.driverRole) && (
              <div className={customProps?.newUI || window.$environment.WHITE_THEME ? 'section-wrapper' : ''}>
                <SubHeader
                  className="subheaderSection spireonDriver notspireonNew roleSection"
                  text={i18n.t('common.departments')}
                  variant="h2"
                />
                <div className="formWrap roleSection">
                  <Department
                    values={values}
                    handleEdit={handleEdit}
                    i18n={i18n}
                    fetchTerminals={customProps.fetchTerminals}
                    fetchDepartments={customProps.fetchDepartments}
                    fetchSubDepartments={customProps.fetchSubDepartments}
                    regions={regions}
                    terminals={terminals}
                    departments={departments}
                    subDepartments={subDepartments}
                    viewOnly={checkIsDefaultFM()}
                    disabledCriteria={disabledCriteria}
                  />
                </div>
              </div>
            )}
          {values.mechanicRole &&
          !hasRestrictedRole([ROLES.SERVICE_MANAGER]) &&
          checkServiceProvider('render_vehicleForMechanic', SERVICE_PROVIDER, false) ? (
            <>
              <div className={customProps?.newUI || window.$environment.WHITE_THEME ? 'section-wrapper' : ''}>
                <SubHeader
                  className="subheaderSection spireonDriver notspireonNew"
                  text={i18n.t('users.vehicleForMechanic')}
                  variant="h2"
                />
                <div className="formWrap notspireonNew">
                  <FormStyle.default.FieldsWrapper width="100%" className="vehicleidMechanic">
                    <Label variant="body1">{i18n.t('common.vehicleId')}</Label>
                    <FormStyle.default.TextWrapper className="userForm">
                      <Field
                        name="vehicleId"
                        onChange={(event) => {
                          handleEdit(event, {
                            field: 'idsPubVehiclesOfMechanic',
                          });
                        }}
                        as={Select}
                        isMulti
                        isClearable
                        placeholder={i18n.t('common.pleaseSelect')}
                        value={values.idsPubVehiclesOfMechanic}
                        suggestions={customProps.vehicleMechanicList}
                        disabled={customProps.viewOnly || checkIsDefaultFM() || false}
                      />
                    </FormStyle.default.TextWrapper>
                  </FormStyle.default.FieldsWrapper>
                </div>
              </div>
            </>
          ) : null}
        </Styled.LeftWrapper>
        <Styled.RightWrapper>
          {isDVIRFlagEnabled && !hasRestrictedRole([ROLES.SERVICE_MANAGER]) && (
            <>
              <SubHeader
                className="subheaderSection spireonDriver notspireonNew roleSection"
                text={i18n.t('common.departments')}
                variant="h2"
              />
              <div className="formWrap roleSection">
                <Department
                  values={values}
                  handleEdit={handleEdit}
                  i18n={i18n}
                  fetchTerminals={customProps.fetchTerminals}
                  fetchDepartments={customProps.fetchDepartments}
                  fetchSubDepartments={customProps.fetchSubDepartments}
                  viewOnly={checkIsDefaultFM()}
                  regions={regions}
                  terminals={terminals}
                  departments={departments}
                  subDepartments={subDepartments}
                  disabledCriteria={disabledCriteria}
                />
              </div>
            </>
          )}
          {!isDVIRFlagEnabled &&
            (!customProps.viewOnly || (customProps.viewOnly && isDocumentAvailable)) &&
            checkServiceProvider('render_documents', SERVICE_PROVIDER, false) && (
              <div className={customProps?.newUI || window.$environment.WHITE_THEME ? 'section-wrapper' : ''}>
                <SubHeader
                  className="subheaderSection documentsHeader spireonDriver"
                  text={i18n.t('common.documents')}
                  variant="h2"
                />
                <Styled.documentWrapper>
                  <MemoizedUploadModule
                    values={values}
                    userId={values.id}
                    type={type}
                    callback={customProps.callback}
                    viewOnly={customProps?.viewOnly || checkIsDefaultFM()}
                    setIsDocumentAvailable={setIsDocumentAvailable}
                    setUploadList={setUploadList}
                    uploadList={uploadList}
                  />
                </Styled.documentWrapper>
              </div>
            )}
          {customProps.showPolicies &&
            layout !== 'Clutch' &&
            (!customProps.viewOnly || (customProps.viewOnly && customProps.policies.length > 0)) && (
              <Styled.RightWrapper>
                <SubHeader
                  className="subheaderSection policyHeader spireonDriver"
                  text={i18n.t('common.policiesAndProcedures')}
                  variant="h2"
                />
                <Policies list={customProps.policies} handleDownloadDoc={customProps.handleDownloadDoc} Styled={Styled} />
              </Styled.RightWrapper>
            )}
          {token?.roles?.includes('FLMANAGER') &&
            token?.enterpriseFeatureDto?.cycleRuleEnabled &&
            values.driverRole &&
            !isDVIRFlagEnabled && (
              <div className={customProps?.newUI || window.$environment.WHITE_THEME ? 'section-wrapper' : ''}>
                <SubHeader text={i18n.t('common.cycleRule')} className="subheaderSection spireonDriver spnocycle" variant="h2" />
                <FormStyle.default.FieldsWrapper className="spnocycle">
                  <Label htmlFor="assignedCycleRules" variant="body1">
                    {i18n.t('common.cycleRule')}
                  </Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="assignedCycleRules"
                      id="assignedCycleRules"
                      disabled={checkIsDefaultFM()}
                      onChange={(event) => {
                        if (
                          !event ||
                          !event.length ||
                          (event.length && !hasAnyValueMatch(event, ['USA 60h/7 days', 'USA 70h/8 days', 'Texas 70h/7 day']))
                        ) {
                          handleEdit('', { field: 'assignedCycleExceptions' });
                          handleEdit(allVehicleTypes[0], {
                            field: 'assignedVehicleType'
                          });
                        }
                        if (
                          event &&
                          event.length &&
                          hasAnyValueMatch(event, ['USA 60h/7 days', 'USA 70h/8 days']) &&
                          hasAnyValueMatch(event, [
                            'Local Radius',
                            'Texas 70h/7 day',
                            'Canada 70h/7 day South',
                            'Canada 120h/14 day South',
                            'Canada 70h/7 day North',
                            'Canada 120h/14 day North',
                            'Mexico',
                          ])
                        ) {
                          handleEdit(allVehicleTypes[0], {
                            field: 'assignedVehicleType',
                          });
                        }
                        if (!event || !event.length) {
                          handleEdit(allVehicleTypes[0], {
                            field: 'assignedVehicleType',
                          });
                        }
                        handleEdit(event, { field: 'assignedCycleRules' });
                        handleEdit('', { field: 'defaultCycleRule' });
                      }}
                      as={Select}
                      placeholder={i18n.t('common.pleaseSelect')}
                      value={getFilterLocaleValues(values['assignedCycleRules'], i18n)}
                      suggestions={getFilterLocaleValues(allCycleRules, i18n)}
                      isClearable
                      isMulti
                    />
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>

                <FormStyle.default.FieldsWrapper className="spnocycle">
                  <Label htmlFor="assignedCycleExceptions" variant="body1">
                    {i18n.t('companyData.exceptions')}
                  </Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="assignedCycleExceptions"
                      id="assignedCycleExceptions"
                      onChange={(event) => {
                        handleEdit(event, { field: 'assignedCycleExceptions' });
                      }}
                      as={Select}
                      placeholder={i18n.t('common.pleaseSelect')}
                      value={getFilterLocaleValues(values['assignedCycleExceptions'], i18n)}
                      suggestions={getFilterLocaleValues(allCycleExceptions, i18n)}
                      disabled={checkIsDefaultFM()}
                      isDisabled={
                        !values?.assignedCycleRules ||
                        !values?.assignedCycleRules.length ||
                        (values?.assignedCycleRules.length &&
                          !hasAnyValueMatch(values?.assignedCycleRules, ['USA 60h/7 days', 'USA 70h/8 days', 'Texas 70h/7 day']))
                      }
                      isClearable
                      isMulti
                    />
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>

                <FormStyle.default.FieldsWrapper className="spnocycle">
                  <Label htmlFor="defaultCycleRule" variant="body1">
                    {i18n.t('companyData.defaultCycleRule')}
                  </Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="defaultCycleRule"
                      id="defaultCycleRule"
                      onChange={(event) => {
                        handleEdit(event, { field: 'defaultCycleRule' });
                      }}
                      disabled={checkIsDefaultFM()}
                      as={Select}
                      placeholder={i18n.t('common.pleaseSelect')}
                      value={getFilterLocaleValues([values['defaultCycleRule']], i18n)}
                      suggestions={getFilterLocaleValues(values['assignedCycleRules'] || [], i18n)}
                    />
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>

                <FormStyle.default.FieldsWrapper className="spnocycle">
                  <Label htmlFor="assignedVehicleType" variant="body1">
                    {i18n.t('companyData.vehicleType')}
                  </Label>
                  <FormStyle.default.TextWrapper className="userForm">
                    <Field
                      name="assignedVehicleType"
                      id="assignedVehicleType"
                      onChange={(event) => {
                        handleEdit(event, { field: 'assignedVehicleType' });
                      }}
                      as={Select}
                      disabled={checkIsDefaultFM()}
                      placeholder={i18n.t('common.pleaseSelect')}
                      value={getFilterLocaleValues([values['assignedVehicleType']], i18n)}
                      suggestions={getFilterLocaleValues(
                        values?.assignedCycleRules &&
                          values?.assignedCycleRules.length &&
                          (hasAnyValueMatch(values?.assignedCycleRules, ['USA 60h/7 days', 'USA 70h/8 days']) ||
                            underscore.pluck(values?.assignedCycleRules, 'value').find((rule) => rule === 'Local Radius') !==
                              undefined) &&
                          !hasAnyValueMatch(values?.assignedCycleRules, [
                            'Texas 70h/7 day',
                            'Canada 70h/7 day South',
                            'Canada 120h/14 day South',
                            'Canada 70h/7 day North',
                            'Canada 120h/14 day North',
                            'Mexico',
                          ])
                          ? allVehicleTypes
                          : [{ label: 'Property', value: 'Property' }],
                        i18n
                      )}
                    />
                  </FormStyle.default.TextWrapper>
                </FormStyle.default.FieldsWrapper>
              </div>
            )}
        </Styled.RightWrapper>
      </Styled.Wrapper>
      {typeof customProps.hasApiStatus?.errorMessage === 'string' && (
        <Message type="error" asTags message={i18n.t(customProps.hasApiStatus.errorMessage)} />
      )}
      {!customProps.viewOnly && !checkIsDefaultFM() && (
        <Styled.submitUser>
          <FormStyle.default.ButtonsWrapper className="submitSection">
            <Button
              className="buttonStyle"
              type="reset"
              label={i18n.t('common.cancel')}
              onClick={(e) => {
                cancelAction();
              }}
            />
            <Button
              type="submit"
              className="buttonStyle"
              label={i18n.t('common.save')}
              onClick={(e, d) => {
                const validateResponse = validateForm();
                validateResponse.then((data) => {
                  submitAction(data);
                });
              }}
            />
          </FormStyle.default.ButtonsWrapper>
        </Styled.submitUser>
      )}
    </>
  );
};

RenderComponent.propTypes = {
  values: PropTypes.object.isRequired,
  handleEdit: PropTypes.func.isRequired,
  errors: PropTypes.object.isRequired,
  isNotValid: PropTypes.bool.isRequired,
  customProps: PropTypes.object,
  validateForm: PropTypes.func.isRequired,
  submitAction: PropTypes.func.isRequired,
};

export default RenderComponent;
